/**
 * Auth Context
 *
 * Provides authentication state to the app.
 * On mount, checks localStorage for session token set by the OAuth bridge page.
 * If found, validates via API. Falls back to device auth if no session.
 */

import { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import type { AuthUser } from '@/lib/auth-client'
import { getCurrentUser, getStoredUser, navigateToGoogleLogin, logoutUser } from '@/lib/auth-client'
import { identifyUser, clearUser, track } from '@/lib/betterstack'

export interface AuthState {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: () => void
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthState>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  login: () => {},
  logout: async () => {},
  refreshUser: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  // First-run funnel: fire once per page load when a user materializes, so
  // "signed in but never entered the app" bounces are visible in RUM events.
  const loginTracked = useRef(false)

  // Wrap setUser so every auth change also links the Better Stack RUM
  // session to the user — Sessions/Events/replays become searchable by user.
  const setUser = useCallback((u: AuthUser | null) => {
    setUserState(u)
    if (u) {
      identifyUser({
        id: u.id,
        ...(u.email ? { email: u.email } : {}),
        ...(u.name ? { username: u.name } : {}),
      })
      if (!loginTracked.current) {
        loginTracked.current = true
        track('login-ok', { hasEmail: u.email ? 1 : 0 })
      }
    } else {
      clearUser()
    }
  }, [])

  const refreshUser = useCallback(async () => {
    const currentUser = await getCurrentUser()
    setUser(currentUser)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    // Check for cached user from OAuth bridge page immediately
    const cached = getStoredUser()
    if (cached) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUser(cached)
      setIsLoading(false)
    }
    // Then validate with server
    refreshUser()
  }, [refreshUser])

  const login = useCallback(() => {
    navigateToGoogleLogin()
  }, [])

  const logout = useCallback(async () => {
    await logoutUser()
    setUser(null)
  }, [])

  const value = useMemo(() => ({
    user,
    isLoading,
    isAuthenticated: user !== null,
    login,
    logout,
    refreshUser,
  }), [user, isLoading, login, logout, refreshUser])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
